/**
 * js/client-intake-notify.js — "a client finished their form" badges for the
 * Personal Intake and Commercial Intake tools.
 *
 * EAGER (loaded in index.html, precached in sw.js), the js/portal-notify.js
 * twin: the sidebar badges have to count on a cold dashboard, before either
 * plugin's lazy scripts (js/client-intake-link.js) exist.
 *
 * What it counts is deliberately one thing — submissions waiting to be
 * applied. A link being opened, or a code being sent, is visible in the
 * Submissions panel but never lights the sidebar: a badge that flickers every
 * time a client looks at their form is a badge the agency learns to ignore.
 *
 * Where the count shows changed in September 2026: it used to paint its own
 * spans on the Personal Intake and Commercial Intake sidebar entries, which
 * put a "1" on the tool the agent was working IN with nothing on that page to
 * explain it. It now rides the **Client Portal** entry's badge, beside the
 * portal's own uploads / change requests, because that page carries the
 * Client forms card that lists every form sent and what came back.
 * js/portal-notify.js folds these counts into PortalNotify.badgeCount(), which
 * is what dashboard-widgets.js paints; this module only asks for a repaint.
 * The per-kind counts and watermarks are unchanged, so the Submissions panel
 * still clears its own kind, and the Client Portal's forms card clears both.
 *
 * Metadata only: `?view=stats` returns counts and timestamps, no client
 * identifiers, and nothing here reads the vault. The "already seen"
 * watermark is one small JSON object under STORAGE_KEYS.INTAKE_LINK_SEEN in
 * this browser (local, not sensitive, never synced), one ISO timestamp per
 * kind; a value written before the commercial badge existed is a bare ISO
 * string and reads as the personal watermark. Opening a Submissions panel
 * clears that kind's badge until the next submission lands.
 */
window.ClientIntakeNotify = (function () {
    'use strict';

    const ENDPOINT = '/api/intake-links?view=stats';
    const KINDS = Object.freeze(['personal', 'commercial']);
    // The Client Portal entry's span — PortalNotify's badge, shared.
    const BADGE_ID = 'sidebar-portalBadge';

    // Awareness badge, not alerting — the completion email is what gets acted
    // on. 180 s matches PortalNotify.
    const TTL_MS = 180000;
    const MAX_CONSECUTIVE_FAILURES = 3;
    const FIRST_KICK_MS = 4000;

    // count: null means "not known yet" and renders BLANK, never "0" — an empty
    // badge honestly reads as nothing to tell you; a 0 would claim we checked.
    // `count` is the personal badge (the original shape); `counts` carries both.
    let _cache = { count: null, counts: { personal: null, commercial: null }, stats: null, ts: 0 };
    let _inflight = null;
    let _failures = 0;
    let _timer = null;
    let _kick = null;

    const kindOf = (k) => (KINDS.includes(k) ? k : 'personal');
    const isIso = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}/.test(s);

    function seenKey() {
        return (window.STORAGE_KEYS && window.STORAGE_KEYS.INTAKE_LINK_SEEN) || '';
    }

    /** The watermark object, tolerating the pre-commercial bare-string shape. */
    function readSeen() {
        const key = seenKey();
        if (!key) return {};
        let raw = '';
        try { raw = window.localStorage.getItem(key) || ''; } catch (_) { return {}; }
        if (!raw) return {};
        if (raw.charAt(0) !== '{') return { personal: raw };
        try {
            const obj = JSON.parse(raw);
            const out = {};
            KINDS.forEach((k) => { if (obj && isIso(obj[k])) out[k] = obj[k]; });
            return out;
        } catch (_) { return {}; }
    }

    /** ISO string of the latest submission of `kind` this browser has looked at, or ''. */
    function lastSeen(kind) {
        return readSeen()[kindOf(kind)] || '';
    }

    /**
     * Mark everything of `kind` up to `iso` (default: the latest submission we
     * know of, else now) as seen. Never moves the watermark backwards, so a
     * stale render cannot un-see newer submissions. Clears that badge at once.
     * markSeen(iso) — the original one-argument call — means the personal kind.
     */
    function markSeen(kind, iso) {
        if (isIso(kind)) { iso = kind; kind = 'personal'; }
        const k = kindOf(kind);
        const bucket = _cache.stats && _cache.stats[k];
        const stamp = iso
            || (bucket && bucket.latestSubmittedAt)
            || (k === 'personal' && _cache.stats && _cache.stats.latestSubmittedAt)
            || new Date().toISOString();
        const key = seenKey();
        if (key) {
            try {
                const seen = readSeen();
                if (!seen[k] || stamp > seen[k]) {
                    seen[k] = stamp;
                    window.localStorage.setItem(key, JSON.stringify(seen));
                }
            } catch (_) { /* private mode: badge just stays noisy, nothing breaks */ }
        }
        const counts = { ..._cache.counts, [k]: 0 };
        _cache = { ..._cache, counts, count: counts.personal, ts: Date.now() };
        paint();
    }

    /**
     * Badge count for one kind from a stats payload: the submitted count,
     * unless the latest submission is older than that kind's watermark
     * (already looked at). The per-kind latestSubmittedAt is preferred; the
     * top-level one is the pre-commercial API's and means personal.
     */
    function countFromStats(stats, kind) {
        const k = kindOf(kind);
        const bucket = stats && stats[k];
        const submitted = bucket && Number.isFinite(Number(bucket.submitted)) ? Number(bucket.submitted) : 0;
        if (submitted <= 0) return 0;
        const latest = (bucket && bucket.latestSubmittedAt) ? String(bucket.latestSubmittedAt)
            : (k === 'personal' && stats.latestSubmittedAt ? String(stats.latestSubmittedAt) : '');
        const seen = lastSeen(k);
        if (latest && seen && latest <= seen) return 0;
        return submitted;
    }

    /**
     * Both kinds together, the number the Client Portal badge adds to its own.
     * null only while NEITHER kind is known, so a blank badge still means
     * "not measured yet" rather than a zero nobody checked.
     */
    function total() {
        const p = _cache.counts.personal;
        const c = _cache.counts.commercial;
        if (p === null && c === null) return null;
        return (p || 0) + (c || 0);
    }

    /**
     * The badge is the Client Portal entry's, painted by
     * DashboardWidgets.updateBadges() from PortalNotify.badgeCount() (which
     * folds total() in), so a repaint is a request to the dashboard. Without
     * the dashboard (a harness, or a page with no sidebar) write the span
     * directly, through PortalNotify when it is there so the portal's own
     * count is not dropped.
     */
    function paint() {
        if (typeof document === 'undefined') return;
        if (window.DashboardWidgets && typeof DashboardWidgets.updateBadges === 'function') {
            try { DashboardWidgets.updateBadges(); return; } catch (_) { /* fall through to the span */ }
        }
        const badge = document.getElementById(BADGE_ID);
        if (!badge) return;
        const n = (window.PortalNotify && typeof PortalNotify.badgeCount === 'function')
            ? PortalNotify.badgeCount()
            : total();
        badge.textContent = n > 0 ? String(n) : '';
        badge.classList.toggle('badge-warning', n > 0);
    }

    function canPoll() {
        if (_failures >= MAX_CONSECUTIVE_FAILURES) return false;
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
        if (typeof Auth === 'undefined' || !Auth.isSignedIn || typeof Auth.apiFetch !== 'function') return false;
        // The badge lives on an admin-only entry; a non-admin has the
        // dashboard's Client Forms card and the completion email instead, so
        // polling for a count nobody would see is just noise in the logs.
        if (!Auth.isAdmin) return false;
        return true;
    }

    /**
     * Last-known count for `kind` (personal by default), kicking a background
     * refresh when stale. Same single-flight / signed-in / cache-on-failure
     * shape as PortalNotify.
     */
    function badgeCount(onUpdate, kind) {
        const k = kindOf(kind);
        const fresh = _cache.ts && (Date.now() - _cache.ts) < TTL_MS;
        if (fresh || _inflight) return _cache.counts[k];
        if (!canPoll()) return _cache.counts[k];
        _inflight = Auth.apiFetch(ENDPOINT)
            .then((res) => {
                if (!res.ok) throw new Error(`intake stats fetch failed (${res.status})`);
                return res.json();
            })
            .then((data) => {
                const stats = (data && data.stats && typeof data.stats === 'object') ? data.stats : null;
                _failures = 0;
                const counts = { personal: countFromStats(stats, 'personal'), commercial: countFromStats(stats, 'commercial') };
                _cache = { count: counts.personal, counts, stats, ts: Date.now() };
                paint();
                if (typeof onUpdate === 'function') onUpdate();
            })
            // Hold the previous counts on failure rather than dropping to zero:
            // an outage must not read as "all clear".
            .catch((err) => {
                _failures += 1;
                if (_failures === MAX_CONSECUTIVE_FAILURES) {
                    console.warn(
                        `[ClientIntakeNotify] Giving up after ${_failures} failures; open the Client Portal to retry. Last error: ${err?.message || 'unknown'}`,
                    );
                }
                _cache = { ..._cache, ts: Date.now() };
            })
            .finally(() => { _inflight = null; });
        return _cache.counts[k];
    }

    /**
     * Force the next badgeCount() to refetch, and clear the stand-down so a
     * fix takes effect without a reload (opening a tool calls this).
     */
    function invalidate() {
        _failures = 0;
        _cache = { ..._cache, ts: 0 };
    }

    /** Refetch now (if allowed) and repaint. */
    function refresh() {
        invalidate();
        return badgeCount();
    }

    function getStats() {
        return _cache.stats;
    }

    function _tick() {
        // Hidden tab: no poll; start()'s visibilitychange catches up.
        if (typeof document !== 'undefined' && document.hidden) return;
        badgeCount();
        paint();
    }

    /** Self-scheduling: nothing in dashboard-widgets.js asks for these badges. */
    function start() {
        if (_timer || typeof window === 'undefined' || typeof window.setInterval !== 'function') return;
        _timer = window.setInterval(_tick, TTL_MS);
        _kick = window.setTimeout(_tick, FIRST_KICK_MS);
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') _tick();
            });
        }
        window.addEventListener('online', () => { invalidate(); _tick(); });
    }

    /** Stop means stop: the interval AND the pending first kick. */
    function stop() {
        if (_timer) { window.clearInterval(_timer); _timer = null; }
        if (_kick) { window.clearTimeout(_kick); _kick = null; }
    }

    if (typeof document !== 'undefined' && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true });
    } else if (typeof document !== 'undefined') {
        start();
    }

    return {
        lastSeen, markSeen, badgeCount, total, countFromStats, invalidate, refresh, getStats, paint, start, stop,
        BADGE_ID, KINDS, TTL_MS, MAX_CONSECUTIVE_FAILURES,
    };
})();
